// src/pages/userDashboard/Dashboard.js (ou .jsx)
import { useEffect, useMemo, useState } from "react";
import Sidebar from "../../components/Sidebar";
import BalanceCard from "../../components/BalanceCard";
import FinancialServiceTabs from "../../components/FinancialServiceTabs";
import TransactionList from "../../components/TransactionList";
import BottomNav from "../../components/BottomNav";
import TransferSimulator from "../../components/userDashboard/TransferSimulator";
import AdvertisementSection from "../../components/userDashboard/AdvertisementSection";
import MoreWithNGC from "../../components/userDashboard/MoreWithNGC";
import SecurityNotice from "../../components/userDashboard/SecurityNotice";

import axios from "../../utils/axiosInstance";

export default function Dashboard() {
  const [loadingTx, setLoadingTx] = useState(true);
  const [txError, setTxError] = useState("");
  const [transactionsRaw, setTransactionsRaw] = useState([]);

  useEffect(() => {
    let mounted = true;

    const fetchRecentTransactions = async () => {
      setLoadingTx(true);
      setTxError("");

      try {
        // Backend: GET /api/transactions/mine
        // Expected: { transactions: [...] } (your console logs suggest that)
        const res = await axios.get("/api/transactions/mine");

        const list = res?.data?.transactions;
        const safe = Array.isArray(list) ? list : [];

        if (mounted) setTransactionsRaw(safe);
      } catch (err) {
        console.error("❌ Error fetching dashboard transactions:", err?.response?.data || err?.message || err);
        if (mounted) setTxError("Impossible de charger vos transactions récentes.");
      } finally {
        if (mounted) setLoadingTx(false);
      }
    };

    fetchRecentTransactions();

    return () => {
      mounted = false;
    };
  }, []);

  // Sort newest first + keep only recent N
  const recentTransactionsForUI = useMemo(() => {
    const sorted = [...transactionsRaw].sort((a, b) => {
      const da = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const db = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return db - da;
    });

    const recent = sorted.slice(0, 5);

    return recent.map((t) => {
      const name =
        t.recipientName ||
        `${t.recipientFirstName || ""} ${t.recipientLastName || ""}`.trim() ||
        t.beneficiaryName ||
        "—";

      const date = t.createdAt
        ? new Date(t.createdAt).toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "—";

      const amountValue =
        t.amount ??
        t.amountToSend ??
        t.totalToPay ??
        t.calculation?.totalToPay ??
        t.calculation?.amountToSend ??
        "—";

      const amountCurrency = t.currency ?? t.currencyFrom ?? t.calculation?.currencyFrom ?? "";

      // TransactionItem expects {amount} (string is OK)
      const amount = `${amountValue} ${amountCurrency}`.trim();

      // Keep backend status; your TransactionItem styles handle success/pending etc.
      const status = t.status || "pending";

      return {
        id: t.id || t.transactionId || t.transactionNumber,
        name,
        date,
        amount,
        status,
      };
    });
  }, [transactionsRaw]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="flex">
        <Sidebar />

        <main className="flex-1 pb-28 md:pb-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            {/* Header area */}
            <div className="flex items-end justify-between gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                  Tableau de bord
                </h1>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Un aperçu rapide de votre activité
                </p>
              </div>
            </div>

            {/* Balance (keep your current placeholder values if you don't have endpoint yet) */}
            <BalanceCard />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <FinancialServiceTabs />
                <TransferSimulator />

                {/* Recent transactions: keep design + add loading / error wrapper */}
                <section className="space-y-3">
                  {loadingTx ? (
                    <div
                      className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-5"
                      aria-busy="true"
                    >
                      <div className="flex items-center justify-between">
                        <div className="h-5 w-48 rounded bg-black/10 dark:bg-white/10 animate-pulse" />
                        <div className="h-4 w-16 rounded bg-black/10 dark:bg-white/10 animate-pulse" />
                      </div>
                      <div className="mt-4 space-y-2">
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={i}
                            className="h-14 rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 animate-pulse"
                          />
                        ))}
                      </div>
                    </div>
                  ) : txError ? (
                    <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 p-5 text-sm text-rose-700 dark:text-rose-300">
                      {txError}
                    </div>
                  ) : (
                    <TransactionList transactions={recentTransactionsForUI} />
                  )}
                </section>
              </div>

              <div className="space-y-6">
                <AdvertisementSection />
                <MoreWithNGC />
                <SecurityNotice />
              </div>
            </div>
          </div>

          {/* Bottom nav (mobile) */}
          <div className="md:hidden">
            <BottomNav />
          </div>
        </main>
      </div>
    </div>
  );
}
